import { Link } from "react-router-dom";
import { Calendar, ConfigProvider, Spin, Select, Typography, Input, Button, message } from "antd";
import es_ES from "antd/lib/locale/es_ES";
import { ScheduleOutlined, CalendarOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";
import axios from "axios";
import "./CalendarView.css";
import VerificationModal from "./VerificationModal";
import DayEventsModal from "./DayEventsModal";
import moment from "moment";
import "moment/locale/es"; // Import base spanish locale
import { formatControlInterno } from "./calendarUtils";
import CalendarCell from "./CalendarCell";
import EventItem from "./EventItem";

// Configure moment to use Spanish but start week on Sunday
moment.updateLocale("es", {
  week: {
    dow: 0, // Sunday is the first day of the week.
  },
});
moment.locale("es");

// Derived calendar locale from es_ES but forcing Sunday start
const customCalendarLocale = {
  ...es_ES.DatePicker,
  lang: {
    ...es_ES.DatePicker.lang,
    firstDayOfWeek: 0, // 0 = Sunday
  },
};

const { Title, Text } = Typography;

const CARD = {
  background: "#fff",
  border: "0.5px solid rgba(0,0,0,0.08)",
  borderRadius: "12px",
};

const CalendarView = () => {
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedValue, setSelectedValue] = useState(moment());
  const [filters, setFilters] = useState(["visita"]);
  const [controlInternoFilter, setControlInternoFilter] = useState("");

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [selectedVisitas, setSelectedVisitas] = useState([]);
  const [loadingModal, setLoadingModal] = useState(false);

  const [isDayModalVisible, setIsDayModalVisible] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  const onSelect = (value) => {
    if (value.format("YYYY-MM") === selectedValue.format("YYYY-MM")) {
      setSelectedDay(value);
      setIsDayModalVisible(true);
    }
  };

  const onPanelChange = (value) => {
    setSelectedValue(value);
    setSelectedDay(null);
    setIsDayModalVisible(false);
  };

  const fetchCalendarData = () => {
    setLoading(true);
    const promises = [];
    if (filters.includes("visita")) {
      promises.push(
        axios.get(
          `${process.env.REACT_APP_API_URL}/verificaciones/calendario/visitas/${moment(selectedValue).format("YYYY")}/${moment(selectedValue).format("MM")}`
        )
      );
    }

    Promise.all(promises)
      .then((responses) => {
        let dates = [];
        responses.forEach((response) => {
          const newDates = response.data.map((date) => ({
            id: date.id,
            ControlInterno: date.ControlInterno,
            Municipio: date.Municipio,
            Fecha: date.Fecha.substring(0, 10),
            Hora1: date.Hora1,
            Hora2: date.Hora2,
            Tipo: date.Tipo,
            Estado: date.Estado,
          }));
          dates = dates.concat(newDates);
        });

        dates.sort((a, b) => {
          if (a.Hora1 && b.Hora1) {
            return a.Hora1.localeCompare(b.Hora1);
          }
          return 0;
        });

        setDates(dates);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchCalendarData();
  }, [selectedValue, filters]);

  const fetchVerificationDetails = (id, type) => {
    setLoadingModal(true);
    setSelectedVerification(null);
    setSelectedVisitas([]);
    
    axios
      .get(`${process.env.REACT_APP_API_URL}/verificaciones/${id}`)
      .then((response) => {
        if (response.data && response.data.length > 0) {
          setSelectedVerification(response.data[0]);
          if (type === "visita") {
            axios.get(`${process.env.REACT_APP_API_URL}/visitas/${id}`).then((visitaResponse) => {
              setSelectedVisitas(visitaResponse.data);
              setLoadingModal(false);
            }).catch(err => {
              console.error("Error fetching visits:", err);
              setLoadingModal(false);
            });
          } else {
            setLoadingModal(false);
          }
        } else {
          message.error("No se encontró la información de la verificación");
          setLoadingModal(false);
          setIsModalVisible(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching verification data:", error);
        message.error("Error al cargar los detalles");
        setLoadingModal(false);
        setIsModalVisible(false);
      });
  };

  const handleItemClick = (item) => {
    setSelectedItemId(item.id);
    setSelectedItemType(item.Tipo);
    setIsModalVisible(true);
    fetchVerificationDetails(item.id, item.Tipo);
  };

  const dateCellRender = (value) => {
    const listData = dates?.filter(
      (date) =>
        moment(date.Fecha).format("YYYY-MM-DD") === value.format("YYYY-MM-DD") &&
        filters.includes(date.Tipo) &&
        (!controlInternoFilter || (date.ControlInterno && date.ControlInterno.toLowerCase().includes(controlInternoFilter.toLowerCase())))
    );

    return (
      <CalendarCell
        value={value}
        dates={listData}
        visibleTypes={filters}
        handleItemClick={handleItemClick}
      />
    );
  };

  const generalFields = [
    { key: "FechaContrato", label: "Fecha de Contrato" },
    { key: "FechaRecepcion", label: "Fecha de Solicitud" },
    { key: "DescripcionBreve", label: "Descripción Breve" },
    { key: "Monto", label: "Monto" },
  ];
  const clienteFields = [
    { key: "NombreCompletoPV", label: "Razón Social" },
    { key: "Cliente", label: "Cliente" },
  ];
  const instalacionFields = [
    { key: "TensionElectricaSuministro", label: "Tensión Eléctrica de Suministro", suffix: "V" },
    { key: "CargaInstalada", label: "Carga Instalada", suffix: "kW" },
    { key: "AlcanceVerificacion", label: "Alcance de la Verificación", suffix: "kW" },
    { key: "Capacidad", label: "Capacidad de la Subestación", suffix: "kVA" },
  ];
  const visitaFields = [
    { key: "FechaVisita", label: "Fecha de Visita" },
    { key: "HoraInicio", label: "Hora de Inicio" },
    { key: "HoraTermino", label: "Hora de Término" },
  ];

  const getDayEvents = () => {
    if (!selectedDay || !dates) return [];
    return dates.filter(
      (date) =>
        moment(date.Fecha).format("YYYY-MM-DD") === selectedDay.format("YYYY-MM-DD") &&
        filters.includes(date.Tipo) &&
        (!controlInternoFilter || (date.ControlInterno && date.ControlInterno.toLowerCase().includes(controlInternoFilter.toLowerCase())))
    );
  };

  return (
    <ConfigProvider locale={es_ES}>
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        {/* ── Header card ── */}
        <div style={{ ...CARD, padding: 0 }}>
          {/* Title row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              padding: "18px 20px 14px",
              borderBottom: "0.5px solid rgba(0,0,0,0.06)",
              flexWrap: "wrap",
            }}
          >
            {/* Icon badge */}
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: "rgba(24,144,255,0.08)",
                border: "0.5px solid rgba(24,144,255,0.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <CalendarOutlined style={{ fontSize: 17, color: "#1677ff" }} />
            </div>

            {/* Title + subtitle */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: "rgba(0,0,0,0.88)", lineHeight: 1.3 }}>
                Calendario
              </div>
              <div style={{ fontSize: 12, color: "rgba(0,0,0,0.38)", marginTop: 1 }}>
                Seguimiento de visitas y fechas importantes
              </div>
            </div>
          </div>

          {/* Search row */}
          <div style={{ padding: "12px 20px" }}>
            <Input.Search
              placeholder="Filtrar por control interno…"
              value={controlInternoFilter}
              onChange={(e) => setControlInternoFilter(e.target.value)}
              onSearch={(value) => setControlInternoFilter(value)}
              allowClear
              loading={loading}
              style={{ maxWidth: 380 }}
            />
          </div>
        </div>

        <Spin spinning={loading}>
          <div style={{ ...CARD, padding: "24px" }}>
            <Calendar
              value={selectedValue}
              fullscreen={true}
              headerRender={({ value, type, onChange, onTypeChange }) => {
                const monthOptions = [];
                const current = value.clone();
                const localeData = value.localeData();
                const months = [];
                for (let i = 0; i < 12; i++) {
                  current.month(i);
                  months.push(localeData.monthsShort(current));
                }
                for (let index = 0; index < 12; index++) {
                  monthOptions.push(<Select.Option className="month-item" key={`${index}`}>{months[index]}</Select.Option>);
                }
                const month = value.month();
                const year = value.year();
                const options = [];
                for (let i = year - 10; i < year + 10; i += 1) {
                  options.push(<Select.Option key={i} value={i} className="year-item">{i}</Select.Option>);
                }

                return (
                  <div className="custom-calendar-header">
                    <div className="header-title-area">
                      <Title level={4} style={{ margin: 0, color: 'white', textTransform: 'capitalize' }}>
                        {value.format('MMMM YYYY')}
                      </Title>
                    </div>
                    <div className="header-controls-area">
                      <Select size="small" dropdownMatchSelectWidth={false} className="my-year-select" onChange={newYear => onChange(value.clone().year(newYear))} value={String(year)}>
                        {options}
                      </Select>
                      <Button shape="circle" size="small" className="calendar-header-btn" icon={<LeftOutlined />} onClick={() => onChange(value.clone().subtract(1, 'month'))} />
                      <Select size="small" dropdownMatchSelectWidth={false} className="my-month-select" value={String(month)} onChange={selectedMonth => onChange(value.clone().month(parseInt(selectedMonth, 10)))}>
                        {monthOptions}
                      </Select>
                      <Button shape="circle" size="small" className="calendar-header-btn" icon={<RightOutlined />} onClick={() => onChange(value.clone().add(1, 'month'))} />
                    </div>
                  </div>
                );
              }}
              dateCellRender={dateCellRender}
              onSelect={onSelect}
              onPanelChange={onPanelChange}
              locale={customCalendarLocale}
              className="enhanced-calendar"
            />
          </div>
        </Spin>
      </div>

      <VerificationModal
        isModalVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible}
        selectedItemId={selectedItemId}
        selectedItemType={selectedItemType}
        selectedVerification={selectedVerification}
        setSelectedVerification={setSelectedVerification}
        selectedVisitas={selectedVisitas}
        setSelectedVisitas={setSelectedVisitas}
        loadingModal={loadingModal}
        generalFields={generalFields}
        clienteFields={clienteFields}
        instalacionFields={instalacionFields}
        visitaFields={visitaFields}
      />

      <DayEventsModal
        isDayModalVisible={isDayModalVisible}
        setIsDayModalVisible={setIsDayModalVisible}
        selectedDay={selectedDay}
        dayEvents={getDayEvents()}
        onEventClick={(id, type) => {
          setSelectedItemId(id);
          setSelectedItemType(type);
          setIsDayModalVisible(false);
          setIsModalVisible(true);
          fetchVerificationDetails(id, type);
        }}
        onRefresh={fetchCalendarData}
      />
    </ConfigProvider>
  );
};

export default CalendarView;
